import { ReactRenderer } from '@tiptap/react';
import tippy, { type Instance as TippyInstance } from 'tippy.js';
import type { SuggestionOptions, SuggestionProps } from '@tiptap/suggestion';
import { AutocompleteList, type AutocompleteListRef } from '../../components/AutocompleteList';
import type {
  AutocompleteProvider,
  AutocompleteContext,
  AutocompleteListItem,
} from '../../types';
import { computeBlockContext } from './blockContext';
import { exitBlock, insertBlock, insertInlineTag } from './insertHandlebars';

/**
 * Creates a freeform autocomplete item from the user's query.
 * Used when allowFreeformHandlebars is enabled and the provider doesn't recognize the syntax.
 */
function createFreeformItem(query: string): AutocompleteListItem {
  const trimmedQuery = query.trim();

  // Detect block start pattern: #blockType [args...]
  if (trimmedQuery.startsWith('#')) {
    const parts = trimmedQuery.slice(1).split(/\s+/);
    const blockType = parts[0];
    const variable = parts.slice(1).join(' ');
    return {
      id: '__freeform_block__',
      label: `{{#${blockType}${variable ? ' ' + variable : ''}}}`,
      value: variable,
      description: 'Insert as block helper',
      isBlockType: true,
      blockType: blockType,
      isComplete: true,
    };
  }

  // Detect block end pattern: /blockType
  if (trimmedQuery.startsWith('/')) {
    const blockType = trimmedQuery.slice(1).trim();
    return {
      id: '__freeform_block_end__',
      label: `{{/${blockType}}}`,
      value: blockType,
      description: 'Insert closing tag',
      isComplete: true,
      metadata: { isBlockEnd: true, blockType },
    };
  }

  // Regular inline tag (could include @root, helpers with args, etc.)
  return {
    id: '__freeform__',
    label: `{{${trimmedQuery}}}`,
    value: trimmedQuery,
    description: 'Insert as template expression',
    isComplete: true,
  };
}

export interface HandlebarsSuggestionOptions {
  provider: AutocompleteProvider;
  /** Show friendly descriptions instead of {{variable}} syntax (default: false) */
  showDescriptions?: boolean;
  /** When true, allows any Handlebars syntax without provider validation (default: false) */
  allowFreeformHandlebars?: boolean;
}

export function createHandlebarsSuggestion(
  options: HandlebarsSuggestionOptions
): Partial<SuggestionOptions<AutocompleteListItem>> {
  // Counter to discard stale async results when the user types faster than
  // the provider responds (Tiptap's suggestion plugin has a shared-state race).
  let requestCounter = 0;

  return {
    char: '{{',
    allowSpaces: true,
    allowedPrefixes: null,

    items: async ({ query, editor }): Promise<AutocompleteListItem[]> => {
      const myRequestId = ++requestCounter;

      // Strip trailing } if user is typing the closing braces
      // This allows the provider to match against the actual variable name
      let cleanQuery = query;
      if (cleanQuery.endsWith('}')) {
        cleanQuery = cleanQuery.slice(0, -1);
      }

      const words = cleanQuery.trim().split(/\s+/).filter(Boolean);
      const currentWord = words[words.length - 1] || '';
      const previousWords = words.slice(0, -1);

      // Compute block nesting context
      const blockContext = computeBlockContext(editor);

      const context: AutocompleteContext = {
        fullText: cleanQuery,
        currentWord,
        previousWords,
        editor,
        blockContext,
      };

      try {
        const response = await options.provider.onQuery(cleanQuery, context);

        // A newer keystroke initiated a new request; discard these stale results.
        if (myRequestId !== requestCounter) {
          return [];
        }

        if (response.isComplete) {
          // Preserve the first item's properties (especially isBlockType/blockType for blocks)
          const firstItem = response.items[0];
          const completeItem = {
            id: '__complete__',
            label: firstItem?.label || query,
            value: firstItem?.value || query,
            description: firstItem?.description,
            isBlockType: firstItem?.isBlockType,
            blockType: firstItem?.blockType,
            nonEditable: firstItem?.nonEditable,
            isComplete: true,
          };
          console.log('isComplete response, returning item:', completeItem);
          return [completeItem];
        }

        // In freeform mode, allow accepting any typed input
        if (options.allowFreeformHandlebars) {
          // If provider returned valid items, show them
          if (response.items.length > 0 && !response.error) {
            return response.items.map((item) => ({
              ...item,
              isComplete: false,
              isError: false,
            }));
          }

          // If no valid items or error, create a freeform item
          if (cleanQuery.trim()) {
            const freeformItem = createFreeformItem(cleanQuery);
            if (response.error) {
              // Show both freeform option and warning
              return [
                freeformItem,
                {
                  id: '__warning__',
                  label: 'Warning',
                  description: response.error,
                  value: '',
                  isError: true,
                },
              ];
            }
            return [freeformItem];
          }
        }

        if (response.error) {
          return [
            {
              id: '__error__',
              label: 'Error',
              description: response.error,
              value: '',
              isError: true,
            },
          ];
        }

        let filtered = response.items;

        // When freeform is off and the user has typed something, filter out
        // items that don't match the query so stale / unrelated suggestions
        // (e.g. block helpers like #if) aren't shown for unrelated input.
        if (!options.allowFreeformHandlebars && cleanQuery.trim()) {
          const q = cleanQuery.trim().toLowerCase();
          filtered = response.items.filter((item) => {
            const label = (item.label || '').toLowerCase();
            const value = (item.value || '').toLowerCase();
            return label.includes(q) || value.includes(q) || q.includes(label);
          });
        }

        return filtered.map((item) => ({
          ...item,
          isComplete: false,
          isError: false,
        }));
      } catch (err) {
        return [
          {
            id: '__error__',
            label: 'Error',
            description: err instanceof Error ? err.message : 'Failed to load suggestions',
            value: '',
            isError: true,
          },
        ];
      }
    },

    command: ({ editor, range, props }) => {
      console.log('Command called with props:', props);

      if (props.isError) {
        return;
      }

      // Freeform items have placeholder descriptions (e.g. "Insert as block helper")
      // meant for the dropdown, not for persisting on the node.
      const isFreeform = typeof props.id === 'string' && props.id.startsWith('__freeform');
      const insertOptions = { showDescriptions: isFreeform ? false : options.showDescriptions };

      // Handle freeform block end tags: exit the nearest ancestor block
      if (props.metadata?.isBlockEnd) {
        const blockType = props.metadata.blockType as string;
        exitBlock(editor, range, blockType);
        return;
      }

      // Handle block types (insert start + end with content space between)
      if (props.isBlockType && props.blockType) {
        console.log('Creating block:', props.blockType, 'with value:', props.value);
        insertBlock(editor, range, props, insertOptions);
        return;
      }

      // Insert the handlebars tag node
      insertInlineTag(editor, range, props, insertOptions);
    },

    render: () => {
      let component: ReactRenderer<AutocompleteListRef> | null = null;
      let popup: TippyInstance[] | null = null;
      // Track current suggestion props for }} completion
      let currentProps: SuggestionProps<AutocompleteListItem> | null = null;
      // Track query synchronously — onBeforeUpdate fires before the async
      // items() call, so this is always up-to-date when onKeyDown runs.
      let latestQuery = '';

      return {
        onBeforeStart: (props: SuggestionProps<AutocompleteListItem>) => {
          latestQuery = props.query;
        },

        onBeforeUpdate: (props: SuggestionProps<AutocompleteListItem>) => {
          latestQuery = props.query;
        },

        onStart: (props: SuggestionProps<AutocompleteListItem>) => {
          currentProps = props;
          component = new ReactRenderer(AutocompleteList, {
            props: {
              items: props.items,
              command: props.command,
            },
            editor: props.editor,
          });

          if (!props.clientRect) {
            return;
          }

          popup = tippy('body', {
            getReferenceClientRect: props.clientRect as () => DOMRect,
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
            maxWidth: 'none',
          });
        },

        onUpdate: (props: SuggestionProps<AutocompleteListItem>) => {
          currentProps = props;
          component?.updateProps({
            items: props.items,
            command: props.command,
          });

          if (!props.clientRect || !popup) {
            return;
          }

          // Hide popup when there are no selectable items (non-freeform mode)
          const hasSelectableItems = props.items.some(item => !item.isError);
          if (!hasSelectableItems && !options.allowFreeformHandlebars) {
            popup[0].hide();
            return;
          }

          popup[0].show();
          popup[0].setProps({
            getReferenceClientRect: props.clientRect as () => DOMRect,
          });
        },

        onKeyDown: (props: { event: KeyboardEvent }): boolean => {
          if (props.event.key === 'Escape') {
            popup?.[0]?.hide();
            return true;
          }

          // Space or Tab: auto-complete the first match into the query
          // and keep the popup open so the user can continue building
          // the expression (e.g. adding helper arguments).
          if ((props.event.key === ' ' || props.event.key === 'Tab') && currentProps) {
            const query = latestQuery.trim();
            const selectableItems = currentProps.items.filter(
              (item) => !item.isError
            );

            if (selectableItems.length > 0 && query) {
              const firstItem = selectableItems[0];

              // Determine the text to auto-complete with
              const completionText = firstItem.isBlockType && firstItem.blockType
                ? `#${firstItem.blockType}`
                : firstItem.value;

              if (completionText && completionText.toLowerCase() !== query.toLowerCase()) {
                props.event.preventDefault();

                const { editor, range } = currentProps;
                const { tr } = editor.state;
                // Replace only the query text (after the {{ trigger)
                const triggerLength = 2; // '{{'
                tr.insertText(completionText + ' ', range.from + triggerLength, range.to);
                editor.view.dispatch(tr);
                return true;
              }
            }
          }

          // Detect }} to complete the tag (user types closing braces).
          // Use latestQuery (set synchronously via onBeforeUpdate) rather than
          // currentProps.query which may be stale if the async items() call
          // for the first } hasn't resolved yet.
          if (props.event.key === '}' && currentProps) {
            const query = latestQuery;
            // If query already ends with }, this keystroke completes }}
            if (query.endsWith('}')) {
              const cleanQuery = query.slice(0, -1).trim(); // Remove trailing }
              let selectableItem: AutocompleteListItem | undefined;

              // In freeform mode, create item for exactly what the user typed
              if (options.allowFreeformHandlebars && cleanQuery) {
                selectableItem = createFreeformItem(cleanQuery);
              } else {
                // Non-freeform mode: only select if provider confirmed a complete match
                selectableItem = currentProps.items.find(item => !item.isError && item.isComplete);
              }

              if (selectableItem) {
                props.event.preventDefault();
                currentProps.command(selectableItem);
                return true;
              }
            }
          }

          return component?.ref?.onKeyDown(props) ?? false;
        },

        onExit: () => {
          popup?.[0]?.destroy();
          component?.destroy();
          currentProps = null;

          // No editing state to clear for wrapping blocks
        },
      };
    },
  };
}
